import { useCallback, useEffect, useState } from 'react'

import { apiGetDashboardOverview } from '../api/overview/overviewApi'
import type { DashboardOverview } from '../api/overview/overviewTypes'

type OverviewState = {
  data: DashboardOverview | null
  loading: boolean
  error: string | null
  warning: string | null
}

/**
 * Loads the first dashboard page payload.
 * Uses mock data while USE_DASHBOARD_REAL_BACKEND is false.
 */
export const useDashboardOverview = () => {
  const [state, setState] = useState<OverviewState>({
    data: null,
    loading: true,
    error: null,
    warning: null,
  })

  const load = useCallback(async () => {
    setState((current) => ({
      ...current,
      loading: true,
      error: null,
      warning: null,
    }))

    const response = await apiGetDashboardOverview()

    if (response.success && response.data) {
      setState({
        data: response.data,
        loading: false,
        error: null,
        warning: response.warning ?? null,
      })
      return
    }

    setState({
      data: null,
      loading: false,
      error: response.message || 'Failed to load dashboard overview.',
      warning: response.warning ?? null,
    })
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  return {
    overview: state.data,
    loading: state.loading,
    error: state.error,
    warning: state.warning,
    reload: load,
  }
}
